/**
 * Who is signed in, asked once and shared by every screen.
 *
 * `status` distinguishes the three states a component actually has to draw
 * differently: still asking, signed out, signed in. A boolean would collapse
 * the first two and flash the sign-in screen at somebody who is already signed
 * in, on every load.
 */

import { useCallback, useEffect, useState } from "react";
import type { SessionUser } from "@iltocat/shared";
import { ApiError, api } from "./api.js";
import { reconcileAccount } from "./activeInventory.js";
import { forgetModelKey } from "./modelKey.js";
import { clearQueue } from "./offlineQueue.js";

export type SessionStatus = "loading" | "in" | "out";

export interface Session {
  status: SessionStatus;
  user: SessionUser | null;
  /** Set when the worker itself is not configured -- the deploy is half done. */
  unconfigured: string[] | null;
  refresh: () => Promise<void>;
  signOut: (everywhere?: boolean) => Promise<void>;
  /**
   * The account's storage, after an upload said what it now is. Applied locally
   * rather than by asking `/api/me` again: the worker has just answered the
   * question, and a capture session uploads a photo every few seconds.
   */
  setUsedBytes: (bytes: number) => void;
}

export function useSession(): Session {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [unconfigured, setUnconfigured] = useState<string[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user: next } = await api.me();
      // A magic link followed while another account's session was still live
      // never goes through signOut() below, so its offline queue and its
      // choice of inventory are still this browser's from before -- both are
      // per-device state that must not leak from one account to the next.
      const switched = next !== null && reconcileAccount(next.id);
      if (switched) await clearQueue();
      // A model key is somebody's money. It goes with whoever entered it: on
      // an account switch, and whenever nobody is signed in here at all.
      if (switched || !next) forgetModelKey();
      setUser(next);
      setUnconfigured(null);
      setStatus(next ? "in" : "out");
    } catch (error) {
      // A 503 with `missing` is not a signed-out visitor, and showing them a
      // sign-in form that cannot work is how illgati's worst deploy felt from
      // the outside.
      if (error instanceof ApiError && error.missing) setUnconfigured(error.missing);
      setUser(null);
      setStatus("out");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** `everywhere` ends every session the account holds, on every device, not just this one. */
  const signOut = useCallback(
    async (everywhere = false) => {
      try {
        await (everywhere ? api.signOutEverywhere() : api.signOut());
        // Only once the worker has actually ended the session -- a second person
        // on this device must never inherit an offline queue that belongs to
        // whoever was signed in before them. Not in the `finally` below: a
        // sign-out that failed (offline, say) has not really happened, and this
        // device's still-unsent captures are not this session's to discard.
        await clearQueue();
      } finally {
        await refresh();
      }
    },
    [refresh],
  );

  const setUsedBytes = useCallback((bytes: number) => {
    setUser((current) => (current ? { ...current, usedBytes: bytes } : current));
  }, []);

  return { status, user, unconfigured, refresh, signOut, setUsedBytes };
}
